/**
 * useAdminAuth — single source for the admin auth header used across the
 * Admin Console.
 *
 * Centralises the sessionStorage read so we have one place to change auth
 * strategy in the future (e.g. migrating to HTTP-only cookies, which would
 * make the token completely invisible to JS at the cost of a dedicated
 * CSRF flow). For an internal personal-use admin console, sessionStorage
 * is acceptable: the threat model doesn't include hostile XSS payloads
 * because the app has no user-generated rendered content.
 *
 * Returns a memoised `{ Authorization: 'Bearer ...' }` so consumers can
 * pass the same object reference across renders (avoids unnecessary
 * re-renders in dialogs that take `extraHeaders` as a prop).
 */
import { useMemo, useSyncExternalStore } from "react";

const TOKEN_KEY = "admin_token";


function getToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || "";
  } catch {
    // sessionStorage can throw under private browsing / locked-down
    // cookie policies. "no token" is a legitimate fallback state.
    return "";
  }
}


// Subscribe to storage events so the header updates if another tab
// logs in/out. Same-tab changes go through `setAdminToken()` below
// which dispatches a synthetic event.
function subscribe(cb) {
  const handler = (e) => {
    if (!e || e.key === TOKEN_KEY || e.type === "admin-token-change") cb();
  };
  window.addEventListener("storage", handler);
  window.addEventListener("admin-token-change", handler);
  return () => {
    window.removeEventListener("storage", handler);
    window.removeEventListener("admin-token-change", handler);
  };
}


export function useAdminAuth() {
  const token = useSyncExternalStore(subscribe, getToken, () => "");
  const authHeader = useMemo(
    // Google-authenticated admins have no Bearer token — they ride the
    // httpOnly session cookie — so send no header at all rather than an
    // empty `Bearer `.
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );
  return { token, authHeader };
}


export function setAdminToken(value) {
  try {
    if (value) sessionStorage.setItem(TOKEN_KEY, value);
    else sessionStorage.removeItem(TOKEN_KEY);
    window.dispatchEvent(new Event("admin-token-change"));
  } catch (err) {
    // Private browsing or quota issue — degrade gracefully.
    console.warn("[useAdminAuth] sessionStorage write failed:", err?.message || err);
  }
}
